import { formatDisplayNumber } from '../../../../utils/numberFormat';

/**
 * Get the x-axis field name from data.
 * Supports: label, date, name, category — picks whichever exists.
 */
export function getXField(data) {
  if (!data || data.length === 0) return 'label';
  const first = data[0];
  if (first.label != null) return 'label';
  if (first.date != null) return 'date';
  if (first.name != null) return 'name';
  if (first.category != null) return 'category';
  // Fallback: first non-'value' string key
  for (const key of Object.keys(first)) {
    if (key !== 'value' && key !== 'series' && key !== 'change' && typeof first[key] === 'string') {
      return key;
    }
  }
  return 'label';
}

/**
 * True when the data carries a second dimension (`series`) to break each bar into —
 * i.e. a stacked or grouped bar. Flat single-dimension data has no non-empty series.
 */
export function hasSeries(data) {
  if (!data || data.length === 0) return false;
  return data.some((d) => d.series != null && d.series !== '');
}

/**
 * Pivot flat category-value items ({label, value, series}) into a per-label layout
 * for stacked/grouped bars. Returns first-seen-ordered `labels` and `seriesKeys`
 * plus `rows` — one object per label carrying a numeric column per series (missing
 * combinations filled with 0). This replaces feeding duplicate labels straight to a
 * d3 scaleBand, which silently deduped them and collapsed the chart (PRD-1370 #13).
 */
export function buildSeriesLayout(data) {
  const labels = [];
  const seriesKeys = [];
  const byLabel = new Map();
  (data || []).forEach((d) => {
    const label = String(d.label != null ? d.label : getX(d));
    const series = String(d.series != null ? d.series : '');
    if (!byLabel.has(label)) {
      labels.push(label);
      byLabel.set(label, { label });
    }
    if (series !== '' && !seriesKeys.includes(series)) seriesKeys.push(series);
    const value = typeof d.value === 'number' ? d.value : Number(d.value) || 0;
    byLabel.get(label)[series] = (byLabel.get(label)[series] || 0) + value;
  });
  const rows = labels.map((label) => {
    const row = byLabel.get(label);
    seriesKeys.forEach((key) => {
      if (row[key] == null) row[key] = 0;
    });
    return row;
  });
  return { labels, seriesKeys, rows };
}

/**
 * Get x-axis value from a data item, with fallback.
 * When data has date values, prefer label/name/category over the date field for x-axis.
 */
export function getX(d) {
  // If item has both label and date, always use label (date goes on y-axis)
  if (d.label) return d.label;
  if (d.name) return d.name;
  if (d.category) return d.category;
  // Only use date as x-axis label if nothing else exists
  return d.date || '';
}

/**
 * Check if data items have a non-empty YYYY-MM-DD `date` field AND a numeric `value`
 * encoded as days-since-2000-01-01. Empty-string date no longer counts — that was
 * letting plain numeric measures (e.g. 5480 = $5,480 rent) be misread as dates.
 */
export function hasDateValues(data) {
  if (!data || data.length === 0) return false;
  return data.every(
    (d) =>
      typeof d.date === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(d.date) &&
      typeof d.value === 'number' &&
      d.value >= 365 &&
      d.value <= 36500,
  );
}

// Time-bucket labels as the backend sends them: `2026-04-01`, `2026-04-01T00:00:00Z`,
// optionally with seconds, fractions or an offset.
const ISO_LABEL_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The bucket as written. A label names its bucket, so it is read on its own clock and
// never converted: `2026-04-01T00:00:00+01:00` is the Apr 1 bucket, not 23:00 on Mar 31
// in UTC, and `…Z` does not slip into March west of GMT.
function parseIsoLabel(label) {
  const m = ISO_LABEL_RE.exec(String(label));
  if (!m) return null;
  const [y, mo, d, hh = 0, mm = 0, ss = 0] = m.slice(1, 7).map((v) => (v === undefined ? undefined : +v));
  // Date.UTC rolls 2026-02-31 or 25:00 over into the next unit; neither is a date label.
  if (mo < 1 || mo > 12 || hh > 23 || mm > 59 || ss > 59) return null;
  if (new Date(Date.UTC(y, mo - 1, d)).getUTCDate() !== d) return null;
  return { y, mo, d, hh, mm, hasTime: hh !== 0 || mm !== 0 || ss !== 0 };
}

// A bucket with no date: the backend writes a NULL group as "None" and keeps it, so a
// series over a column with gaps (incidents by resolved date) carries one.
const EMPTY_LABELS = new Set(['', 'none', 'null', 'nan', 'nat', 'undefined']);
const isEmptyLabel = (label) => label == null || EMPTY_LABELS.has(String(label).trim().toLowerCase());
const NO_DATE = 'No date';

/**
 * The bucket size of a set of ISO date labels — 'hour' | 'year' | 'month' | 'day' —
 * from where the buckets start. null unless every label is an ISO date/timestamp or an
 * empty bucket (see EMPTY_LABELS), with at least one date. Empty buckets don't count
 * towards the size. Quarters are not inferred: monthly data with gaps (Jan, Apr, Jul)
 * looks the same, and calling a month's bar a quarter misstates it. A lone bucket is
 * shown in full.
 */
export function isoDateGranularity(labels) {
  if (!labels) return null;
  const present = labels.filter((l) => !isEmptyLabel(l));
  if (present.length === 0) return null;
  const dates = present.map(parseIsoLabel);
  if (dates.some((d) => d == null)) return null;
  if (dates.some((d) => d.hasTime)) return 'hour';
  if (new Set(present.map(String)).size < 2) return 'day';
  if (dates.every((d) => d.d === 1 && d.mo === 1) && new Set(dates.map((d) => d.y)).size === dates.length) return 'year';
  if (dates.every((d) => d.d === 1)) return 'month';
  return 'day';
}

/**
 * A tick/tooltip formatter for x labels that are ISO date buckets, or null when
 * they aren't (non-date or mixed labels render unchanged). An empty bucket among
 * dates reads "No date".
 */
export function isoDateLabelFormatter(labels) {
  const unit = isoDateGranularity(labels);
  if (!unit) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return (label) => {
    if (isEmptyLabel(label)) return NO_DATE;
    const d = parseIsoLabel(label);
    if (!d) return String(label);
    const mon = MONTHS[d.mo - 1];
    switch (unit) {
      case 'year': return String(d.y);
      case 'month': return `${mon} ${d.y}`;
      case 'hour': return `${mon} ${d.d}, ${d.y} ${pad(d.hh)}:${pad(d.mm)}`;
      default: return `${mon} ${d.d}, ${d.y}`;
    }
  };
}

/**
 * How each renderer shows its x labels: ISO date buckets read as dates
 * ("Apr 2026"), anything else as sent. One place, so every chart type agrees.
 */
export function xLabelDisplay(labels) {
  const format = isoDateLabelFormatter(labels);
  return (v) => (format ? format(v) : String(v));
}

/**
 * Compute a smart y-axis minimum. If all values are clustered high
 * (min > 50% of max), start from below the min so differences are visible.
 *
 * Single-point / zero-range case: anchor yMin at 0 so the bar has visible
 * height. Otherwise yMin would collapse to the value itself and the bar
 * would render with height = 0 (invisible).
 */
export function smartYMin(data) {
  if (!data || data.length === 0) return 0;
  const min = Math.min(...data.map((d) => d.value));
  const max = Math.max(...data.map((d) => d.value));
  if (min === max) return min > 0 ? 0 : min - 1;
  if (min > max * 0.5 && min > 0) {
    const range = max - min;
    return Math.max(0, min - range * 0.3);
  }
  return 0;
}

/**
 * Convert days since 2000-01-01 to a formatted date string (e.g. "Jun 2020").
 */
export function daysToDate(days) {
  const base = new Date(2000, 0, 1);
  const date = new Date(base.getTime() + days * 86400000);
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * A number as a chart shows it — axis ticks, value labels, tooltips, pie
 * percentages: at most two decimals, the console-wide display rule. The
 * separators stay en-US, which is what the charts have always drawn.
 */
export function formatChartNumber(value) {
  return formatDisplayNumber(value, 'en-US') ?? '';
}

/**
 * Get the display value for y-axis — actual date if date data, otherwise formatted number.
 */
export function formatYValue(value, isDateData) {
  if (isDateData) return daysToDate(value);
  return formatDisplayNumber(value) ?? String(value);
}

/**
 * Truncate a long X-axis tick label so the rotated text doesn't overflow
 * the chart bounds. Adds a single-char "…" so the truncation is visible.
 * Returns input unchanged if it's already short enough.
 */
export function truncateLabel(text, maxLen = 22) {
  if (text == null) return '';
  const s = String(text);
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen - 1) + '…';
}

/**
 * Split a horizontal tick label onto up to two lines so it fits in
 * `maxCharsPerLine`. Used by the renderers when ticks are horizontal —
 * wrapping reads better than truncating with "…".
 *  - Already short enough → one line, unchanged.
 *  - Multi-word + a clean split exists → two balanced lines.
 *  - Single long word or no clean split → truncate with "…".
 */
export function tickLabelLines(label, maxCharsPerLine) {
  if (label == null) return [''];
  const s = String(label);
  if (s.length <= maxCharsPerLine) return [s];

  const trunc = (str) =>
    str.length <= maxCharsPerLine ? str : str.slice(0, Math.max(1, maxCharsPerLine - 1)) + '…';

  const words = s.split(/\s+/).filter(Boolean);
  if (words.length < 2) return [trunc(s)];

  let bestSplit = -1;
  let bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const left = words.slice(0, i).join(' ');
    const right = words.slice(i).join(' ');
    if (left.length <= maxCharsPerLine && right.length <= maxCharsPerLine) {
      const diff = Math.abs(left.length - right.length);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestSplit = i;
      }
    }
  }
  if (bestSplit > 0) {
    return [words.slice(0, bestSplit).join(' '), words.slice(bestSplit).join(' ')];
  }

  // No clean split — pick the most even one and truncate each line.
  let centerSplit = 1;
  let centerDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const diff = Math.abs(
      words.slice(0, i).join(' ').length - words.slice(i).join(' ').length,
    );
    if (diff < centerDiff) {
      centerDiff = diff;
      centerSplit = i;
    }
  }
  return [
    trunc(words.slice(0, centerSplit).join(' ')),
    trunc(words.slice(centerSplit).join(' ')),
  ];
}

/**
 * Decide whether the X-axis ticks need to be rotated -45°. Horizontal
 * ticks are nicer to read but overlap when the longest label is wider
 * than the per-tick band. Threshold: 90% of the available band width.
 */
export function xTicksNeedRotation(labels, bandWidth, fontSize = 11) {
  if (!labels || labels.length === 0 || !bandWidth) return false;
  const charWidth = fontSize * 0.55;
  const longestPx = Math.max(...labels.map((l) => String(l).length)) * charWidth;
  return longestPx > bandWidth * 0.9;
}

/**
 * Estimate the vertical pixel extent occupied by tick text, used to
 * size `margin.bottom` and position the X-axis title just below the
 * actual tick labels.
 *  - rotated  : -45° rotated text — height = textWidth * sin(45°)
 *  - horizontal : just one font line + small buffer
 */
export function xTickHeight(labels, fontSize = 11, rotated = true) {
  if (!labels || labels.length === 0) return 0;
  if (!rotated) return fontSize + 4;
  const charWidth = fontSize * 0.55;
  const maxLen = Math.max(...labels.map((l) => String(l).length));
  return Math.ceil(maxLen * charWidth * 0.707) + 6;
}

/**
 * Render the X-axis title centered below the rotated tick labels.
 * `tickHeight` should come from `xTickHeight(...)` so the title lands
 * just under the ticks regardless of how long they are.
 */
export function renderXAxisLabel(g, label, w, h, tickHeight = 60) {
  if (!label) return;
  g.append('text')
    .attr('x', w / 2)
    .attr('y', h + tickHeight + 18)
    .attr('text-anchor', 'middle')
    .attr('font-size', '12px')
    .attr('font-family', 'Roboto, sans-serif')
    .attr('fill', '#757575')
    .text(label);
}

/**
 * Decide whether a Y-axis label needs to be wrapped onto two lines.
 * Used by both the renderer (to bump `margin.left`) and `renderYAxisLabel`
 * (to actually wrap), so they agree on the decision.
 */
export function yLabelNeedsWrap(label, h) {
  if (!label) return false;
  const fontSize = 12;
  const charWidth = fontSize * 0.55;
  const words = label.split(/\s+/).filter(Boolean);
  return label.length * charWidth > h * 0.85 && words.length >= 2;
}

/**
 * Render the Y-axis label rotated -90°. If the label is too long to fit
 * within the chart height, wrap onto a second line at the word boundary
 * closest to the middle. Both lines sit fully within the left margin —
 * line1 closer to the chart (at viewport x ≈ 29), line2 further out
 * (at viewport x ≈ 15) with room for the rotated glyph ascender.
 *
 * The caller is expected to bump `margin.left` by 14 when wrapping so the
 * lines don't crowd the y-tick labels. See `yLabelNeedsWrap`.
 */
export function renderYAxisLabel(g, label, h, marginLeft) {
  if (!label) return;
  const fontSize = 12;
  const baseX = -h / 2;

  const append = (text, y) =>
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', baseX)
      .attr('y', y)
      .attr('text-anchor', 'middle')
      .attr('font-size', `${fontSize}px`)
      .attr('font-family', 'Roboto, sans-serif')
      .attr('fill', '#757575')
      .text(text);

  if (!yLabelNeedsWrap(label, h)) {
    append(label, -marginLeft + 15);
    return;
  }

  // Pick the word boundary that splits the label most evenly by char count
  const words = label.split(/\s+/).filter(Boolean);
  let bestSplit = 1;
  let bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const left = words.slice(0, i).join(' ').length;
    const right = words.slice(i).join(' ').length;
    const diff = Math.abs(left - right);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestSplit = i;
    }
  }
  const line1 = words.slice(0, bestSplit).join(' ');
  const line2 = words.slice(bestSplit).join(' ');

  // viewport x = marginLeft + originalY:
  // line1 at viewport x ≈ 29 (closer to chart), line2 at viewport x ≈ 15
  append(line1, -marginLeft + 29);
  append(line2, -marginLeft + 15);
}

/**
 * Auto-detect axis labels from data fields.
 * Returns { xLabel, yLabel } based on field names present.
 */
export function getAxisLabels(data) {
  if (!data || data.length === 0) return { xLabel: '', yLabel: '' };
  const xField = getXField(data);
  const isDate = hasDateValues(data);

  // X-axis: describe what the labels represent
  const xMap = { label: 'Category', date: 'Date', name: 'Name', category: 'Category' };
  const xLabel = xMap[xField] || 'Category';

  // Y-axis: "Date" if date data, otherwise "Value"
  const yLabel = isDate ? 'Date' : 'Value';

  return { xLabel, yLabel };
}
